import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { dmyTime } from "@aurumiq/ui/lib/format";
import { useState } from "react";
import { Link } from "react-router";
import { PageHeader } from "@/app/page-header";
import { JsonView } from "@/components/json-view";
import { type AuditFilters, useAudit } from "@/features/system/queries";
import { errorMessage } from "@/lib/api";
import type { AuditItem } from "@/lib/types";

const col = createColumnHelper<AuditItem>();
const columns = [
  col.accessor("at", {
    header: "When",
    cell: (c) => <span className="whitespace-nowrap tabular">{dmyTime(c.getValue())}</span>,
  }),
  col.accessor("action", {
    header: "Action",
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
  }),
  col.accessor("actor_label", {
    header: "Actor",
    cell: (c) => (
      <div>
        <div>{c.getValue() ?? c.row.original.actor_id ?? "system"}</div>
        <div className="text-muted-foreground text-xs">{c.row.original.actor_type}</div>
      </div>
    ),
  }),
  col.accessor("target_id", {
    header: "Target",
    cell: (c) =>
      c.getValue() ? (
        <span className="font-mono text-xs">
          {c.row.original.target_type}:{c.getValue()}
        </span>
      ) : (
        "—"
      ),
  }),
  col.accessor("org_id", {
    header: "Org",
    cell: (c) =>
      c.getValue() ? (
        <Link
          to={`/orgs/${c.getValue()}`}
          className="font-mono text-primary text-xs hover:underline"
        >
          {c.getValue()}
        </Link>
      ) : (
        "—"
      ),
  }),
  col.accessor("reason", {
    header: "Reason",
    cell: (c) => c.getValue() ?? <span className="text-muted-foreground">—</span>,
  }),
  col.accessor("meta", {
    header: "Meta",
    cell: (c) => <JsonView value={c.getValue()} label="meta" />,
  }),
] as ColumnDef<AuditItem, unknown>[];

const EMPTY: AuditFilters = { action: "", actor_id: "", org_id: "", from: "", to: "" };

export function AuditPage() {
  const [draft, setDraft] = useState<AuditFilters>(EMPTY);
  const [filters, setFilters] = useState<AuditFilters>(EMPTY);
  const [page, setPage] = useState(1);
  const audit = useAudit({ ...filters, page });
  const total = audit.data?.total ?? 0;
  const pageSize = audit.data?.page_size ?? 25;
  const set = (k: keyof AuditFilters) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every staff action with its reason. Filters apply server-side."
      />
      <DataTable
        columns={columns}
        data={audit.data?.items ?? []}
        isLoading={audit.isPending}
        getRowId={(a) => a.id}
        pageCount={Math.max(1, Math.ceil(total / pageSize))}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={(p) => setPage(p.pageIndex + 1)}
        pageSizeOptions={[pageSize]}
        emptyMessage={
          audit.isError
            ? `Could not load audit log: ${errorMessage(audit.error)}`
            : "No audit entries match."
        }
        toolbar={
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setFilters(draft);
            }}
          >
            <Input
              aria-label="Action"
              placeholder="Action (e.g. org.update)"
              value={draft.action}
              onChange={set("action")}
              className="w-44"
            />
            <Input
              aria-label="Actor"
              placeholder="Actor id"
              value={draft.actor_id}
              onChange={set("actor_id")}
              className="w-36"
            />
            <Input
              aria-label="Organisation"
              placeholder="Org id"
              value={draft.org_id}
              onChange={set("org_id")}
              className="w-36"
            />
            <Input
              aria-label="From"
              type="date"
              value={draft.from}
              onChange={set("from")}
              className="w-40"
            />
            <Input
              aria-label="To"
              type="date"
              value={draft.to}
              onChange={set("to")}
              className="w-40"
            />
            <Button type="submit" variant="outline">
              Apply
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft(EMPTY);
                setFilters(EMPTY);
                setPage(1);
              }}
            >
              Clear
            </Button>
          </form>
        }
      />
    </>
  );
}
